import axiosInstance from "../axiosInstance";

/* =====================
   TYPES  (admin-api /api/tenants/{tenant}/members)
===================== */

export interface MemberRole {
  key: string;
  display_name?: string | null;
  group?: string | null;
}

export interface Member {
  user_id: string;
  username: string;
  email?: string | null;
  email_verified: boolean;
  first_name: string;
  last_name: string;
  display_name: string;
  state: string;
  pending_invite: boolean;
  /** Came in via SSO without an invite; no roles yet. Approve or reject. */
  pending_approval: boolean;
  role_keys: string[];
  grant_id?: string | null;
  mfa_enabled: boolean;
  is_self: boolean;
  manageable: boolean;
}

export interface InviteMemberReq {
  email: string;
  first_name: string;
  last_name: string;
  role_keys: string[];
}

export interface MemberAction {
  user_id: string;
  action: string;
  message: string;
}

export interface SamlSpInfo {
  entity_id: string;
  metadata_url: string;
  acs_url: string;
}

export interface SsoProvider {
  id: string;
  name: string;
  provider: "google" | "microsoft" | "oidc" | "saml" | "other";
  state: string;
  enabled: boolean;
  /** SAML only: what to register at the IdP (known after connecting) */
  saml?: SamlSpInfo | null;
}

export interface SsoSetupInfo {
  redirect_uri: string;
  providers: string[];
}

export interface SsoConnectReq {
  provider: "google" | "microsoft" | "oidc" | "saml";
  name?: string;
  client_id?: string;
  client_secret?: string;
  issuer?: string;
  azure_tenant_id?: string;
  metadata_url?: string;
  metadata_xml?: string;
  signed_requests?: boolean;
}

export interface SignInSettings {
  allow_password: boolean;
  password_reset: boolean;
  force_mfa: boolean;
  force_mfa_local_only: boolean;
  second_factors: string[];
  customised: boolean;
  sso_providers: number;
  sms_available: boolean;
}

export type SignInSettingsUpdate = Pick<SignInSettings, "allow_password" | "password_reset" | "force_mfa" | "force_mfa_local_only" | "second_factors">;

const base = (tenantId: string) => `/api/tenants/${encodeURIComponent(tenantId)}/members`;
const ssoBase = (tenantId: string) => `/api/tenants/${encodeURIComponent(tenantId)}/sso`;

export const membersApi = {
  roles: (tenantId: string) => axiosInstance.get<MemberRole[]>(`${base(tenantId)}/roles`),
  list: (tenantId: string) => axiosInstance.get<Member[]>(base(tenantId)),
  invite: (tenantId: string, data: InviteMemberReq) =>
    axiosInstance.post<Member>(`${base(tenantId)}/invite`, data),
  resendInvite: (tenantId: string, userId: string) =>
    axiosInstance.post<MemberAction>(`${base(tenantId)}/${userId}/resend-invite`),
  updateRoles: (tenantId: string, userId: string, role_keys: string[]) =>
    axiosInstance.put<Member>(`${base(tenantId)}/${userId}/roles`, { role_keys }),
  deactivate: (tenantId: string, userId: string) =>
    axiosInstance.post<MemberAction>(`${base(tenantId)}/${userId}/deactivate`),
  reactivate: (tenantId: string, userId: string) =>
    axiosInstance.post<MemberAction>(`${base(tenantId)}/${userId}/reactivate`),
  remove: (tenantId: string, userId: string) =>
    axiosInstance.delete<MemberAction>(`${base(tenantId)}/${userId}`),
  approve: (tenantId: string, userId: string, role_keys: string[]) =>
    axiosInstance.post<Member>(`${base(tenantId)}/${userId}/approve`, { role_keys }),
  reject: (tenantId: string, userId: string) =>
    axiosInstance.post<MemberAction>(`${base(tenantId)}/${userId}/reject`),
  reset2fa: (tenantId: string, userId: string) =>
    axiosInstance.post<MemberAction>(`${base(tenantId)}/${userId}/reset-2fa`),

  // Single sign-on
  ssoList: (tenantId: string) => axiosInstance.get<SsoProvider[]>(ssoBase(tenantId)),
  ssoSetupInfo: (tenantId: string) => axiosInstance.get<SsoSetupInfo>(`${ssoBase(tenantId)}/setup-info`),
  ssoConnect: (tenantId: string, data: SsoConnectReq) => axiosInstance.post<SsoProvider>(ssoBase(tenantId), data),
  ssoRemove: (tenantId: string, idpId: string) => axiosInstance.delete(`${ssoBase(tenantId)}/${idpId}`),

  // Sign-in methods (org login policy)
  signInSettings: (tenantId: string) => axiosInstance.get<SignInSettings>(`${ssoBase(tenantId)}/sign-in-settings`),
  updateSignInSettings: (tenantId: string, data: SignInSettingsUpdate) =>
    axiosInstance.put<SignInSettings>(`${ssoBase(tenantId)}/sign-in-settings`, data),
  resetSignInSettings: (tenantId: string) => axiosInstance.delete<SignInSettings>(`${ssoBase(tenantId)}/sign-in-settings`),
};

/** Readable text from an admin-api error (string detail, {message}, or pydantic list). */
export function membersErr(err: any, fallback: string): string {
  const d = err?.response?.data?.detail;
  if (!d) return err?.response ? fallback : "Network error";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) {
    const msgs = d.map((e: any) => (typeof e?.msg === "string" ? e.msg.replace(/^Value error, /, "") : "")).filter(Boolean);
    return msgs.join(". ") || fallback;
  }
  if (typeof d === "object") {
    const z = d.zitadel?.message;
    return [d.message, typeof z === "string" ? z : ""].filter(Boolean).join(" — ") || fallback;
  }
  return fallback;
}
